import React, { useContext, createContext, useState, useEffect, useCallback, useRef } from "react";
import { apifetch } from "./services/interceptors";

// Which UI shell the signed-in user gets. The redesigned shell ships alongside the
// existing one rather than replacing it, so this is a per-user choice that has to
// survive a reload, a new browser, and a backend that is briefly unreachable.
//
// The value is owned by the server (users.ui_version) but cached in localStorage.
// That split matters for one reason: the shell has to be chosen on the very first
// paint, and /me has not answered yet at that point. Without the cache the app would
// render the old shell and then visibly swap. The server still wins the moment it
// replies — the cache only decides what to show while waiting.
const UiVersionContext = createContext();

const STORAGE_KEY = "uiVersion";
const VERSIONS = ["v1", "v2"];
const normalise = (v) => (VERSIONS.includes(v) ? v : "v1");

// A URL escape hatch, deliberately read once at module load rather than reactively.
// If the new shell fails to render, ?ui=v1 gets the user working again without a
// database edit and without needing the new shell to be functional enough to click.
// It overrides for this page load only and never writes back.
const urlOverride = (() => {
  try {
    const v = new URLSearchParams(window.location.search).get("ui");
    return VERSIONS.includes(v) ? v : null;
  } catch (e) {
    return null;
  }
})();

export const UiVersionProvider = ({ children }) => {
  const [uiVersion, setUiVersionState] = useState(() => {
    if (urlOverride) return urlOverride;
    try {
      return normalise(localStorage.getItem(STORAGE_KEY));
    } catch (e) {
      return "v1";
    }
  });

  // Keep the cache in step so the next first paint starts on the right shell.
  // The override is excluded on purpose: it is a temporary look, not a choice.
  useEffect(() => {
    if (urlOverride) return;
    try {
      localStorage.setItem(STORAGE_KEY, uiVersion);
    } catch (e) {
      /* private mode, or storage full: the preference still works for this session */
    }
  }, [uiVersion]);

  // Once the user has chosen in this session, their click outranks anything /me says.
  // Without this the two race: flipping the preference swaps the shell, the newly
  // mounted shell re-fetches /me, and if that reply was already in flight it carries
  // the OLD value and silently undoes the click. The database ends up correct while
  // the screen shows the opposite, which is the worst of both.
  const choiceMadeRef = useRef(false);

  // Called by whoever fetches /me. Lets the server correct a stale cache without this
  // module having to own a second /me request. Authoritative only until the user
  // touches the switch; after that it is ignored until the next full page load.
  const syncFromServer = useCallback((serverValue) => {
    if (urlOverride) return;
    if (!serverValue) return;
    if (choiceMadeRef.current) return;
    setUiVersionState(normalise(serverValue));
  }, []);

  // Optimistic: the shell swaps immediately, then we persist. If the write fails we
  // put it back, because silently keeping a preference the server did not record
  // would mean the next sign-in mysteriously reverts.
  const setUiVersion = useCallback(
    async (next) => {
      const target = normalise(next);
      const previous = uiVersion;
      if (target === previous) return true;
      choiceMadeRef.current = true;
      setUiVersionState(target);
      try {
        const token = localStorage.getItem("token");
        const response = await apifetch("s1/api/me/ui-version", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ui_version: target }),
        });
        if (!response.ok) throw new Error(`ui-version save failed: ${response.status}`);
        return true;
      } catch (e) {
        console.error("[uiVersion] could not save preference, reverting", e);
        // The write failed, so there is no stored choice to defend any more.
        choiceMadeRef.current = false;
        setUiVersionState(previous);
        return false;
      }
    },
    [uiVersion]
  );

  return (
    <UiVersionContext.Provider
      value={{ uiVersion, setUiVersion, syncFromServer, isUrlOverridden: !!urlOverride }}
    >
      {children}
    </UiVersionContext.Provider>
  );
};

export const useUiVersion = () => {
  const ctx = useContext(UiVersionContext);
  // Defaulting rather than throwing: a missing provider should degrade to the current
  // UI, not blank the app.
  return ctx || { uiVersion: "v1", setUiVersion: async () => false, syncFromServer: () => {}, isUrlOverridden: false };
};
